import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { Controller, useForm } from "react-hook-form";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import MenuDropdown from "@/components/MenuDropdown";
import TextField from "@/components/TextField";
import Banner from "@/components/Banner";
import Checkbox from "@/components/Checkbox";
import { showSnackbar } from "@/lib/snackbarUtils";
import { CREATE_NON_ACTIVE_CLASSIFICATION_TASK_MUTATION, type LifeMemberClassification } from "../mutations";
import type { LifeMemberEligibilityOption } from "../_utils/nonActiveClassificationRequest";

type FormValues = {
  classification: LifeMemberClassification | "";
  note: string;
  attestationAcknowledged: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  personId: string;
  options: LifeMemberEligibilityOption[];
};

export default function RequestNonActiveClassificationModal({ open, onClose, personId, options }: Props) {
  const [bannerError, setBannerError] = useState("");
  const [createTask, { loading: isSubmitting }] = useMutation(CREATE_NON_ACTIVE_CLASSIFICATION_TASK_MUTATION);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isValid }
  } = useForm<FormValues>({
    defaultValues: {
      classification: "",
      note: "",
      attestationAcknowledged: false
    },
    mode: "onChange"
  });

  const selectedCode = watch("classification");
  const selectedOption = useMemo(
    () => options.find((option) => option.code === selectedCode) ?? null,
    [options, selectedCode]
  );
  const requiresAttestation = selectedOption?.requiresAttestation === true;
  const attestationAcknowledged = watch("attestationAcknowledged");

  useEffect(() => {
    if (open) {
      reset({
        classification: "",
        note: "",
        attestationAcknowledged: false
      });
      setBannerError("");
    }
  }, [open, reset]);

  useEffect(() => {
    setValue("attestationAcknowledged", false);
  }, [selectedCode, setValue]);

  const classificationOptions = useMemo(
    () =>
      options.map((option) => ({
        value: option.code as LifeMemberClassification,
        label: `${option.label} (${option.code})`,
        displayText: option.code
      })),
    [options]
  );

  const canSubmit =
    isValid && Boolean(selectedCode) && (!requiresAttestation || attestationAcknowledged) && !isSubmitting;

  const onSubmit = async (formData: FormValues) => {
    if (!formData.classification) return;

    try {
      setBannerError("");
      const { data } = await createTask({
        variables: {
          personId,
          classification: formData.classification,
          note: formData.note.trim() || null,
          attestationAcknowledged: requiresAttestation ? formData.attestationAcknowledged : null
        }
      });

      const result = data?.createNonActiveClassificationTask;
      if (result?.success) {
        showSnackbar(result.message || "Membership Services follow-up task created.");
        onClose();
        return;
      }

      setBannerError(result?.message || "Failed to create request task.");
    } catch (error) {
      setBannerError(error instanceof Error ? error.message : "Failed to create request task.");
    }
  };

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Request Non-Active Classification"
      className="w-[672px] max-h-[85vh]"
      hasFormElements
      actions={
        <>
          <Button
            variant="text"
            onClick={onClose}
            testId="request-non-active-classification-cancel-button"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSubmit(onSubmit)}
            testId="request-non-active-classification-submit-button"
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {!!bannerError && <Banner inline variant="error" description="The request failed." subText={bannerError} />}

        <Controller
          name="classification"
          control={control}
          rules={{ required: "Classification is required" }}
          render={({ field }) => {
            const selected = classificationOptions.find((option) => option.value === field.value);
            return (
              <MenuDropdown<{ value: LifeMemberClassification; label: string; displayText: string }>
                label="Classification *"
                options={classificationOptions}
                value={selected}
                onSelect={(option) => field.onChange(option.value)}
                getLabel={(option) => option.label}
                renderItem={(option) => <div className="text-body-large">{option.label}</div>}
              />
            );
          }}
        />

        {requiresAttestation && selectedOption && (
          <div className="flex flex-col gap-6">
            {(selectedOption.attestationCopyParagraphs ?? []).map((paragraph) => (
              <p key={paragraph} className="text-body-medium text-sys-brand-on-surface">
                {paragraph}
              </p>
            ))}
            <Controller
              name="attestationAcknowledged"
              control={control}
              render={({ field }) => (
                <Checkbox
                  name="attestationAcknowledged"
                  checked={field.value}
                  onChange={field.onChange}
                  label={
                    selectedOption.attestationAcknowledgementLabel ||
                    "I acknowledge the Life Member Century stipulations."
                  }
                  align="start"
                />
              )}
            />
          </div>
        )}

        <Controller
          name="note"
          control={control}
          render={({ field }) => (
            <TextField
              label="Note (Optional)"
              supportingText="Included on the Membership Services follow-up task"
              value={field.value}
              onChangeText={field.onChange}
            />
          )}
        />
      </div>
    </Dialog>
  );
}
